/**
 * activitySearchFormatter — la recherche des activités faites : les libellés de ses puces et les
 * paramètres qu'elle envoie au serveur.
 *
 * Pure : l'espace parent (design system) et l'écran élève (legacy) la lisent tous les deux, pour
 * que les deux barres cherchent de la même façon.
 */
import { type IconName } from "../../../../design-system/atoms/icons/Icon/iconRegistry"
import { type UIActivityPeriod, type UIActivitySearch, type UIActivityType } from "../types/activity"
import { type UISubject } from "../types/schoolSubject"

/**
 * Un filtre de la barre. `icon` est un nom FontAwesome 5 que le registre du design system dessine
 * aussi (`iconRegistry.ts`) : la même icône côté parent et côté élève, legacy compris.
 */
export type ActivitySearchFilter<K> = { key: K; label: string; icon: IconName }

/** Les types d'activité (`ActivityTemplate::$types`), au pluriel. */
export const ACTIVITY_TYPE_FILTERS: ActivitySearchFilter<UIActivityType>[] = [
    { key: "card", label: "Fiches", icon: "book-open" },
    { key: "written_exercise", label: "Exercices écrits", icon: "pencil-alt" },
    { key: "revision", label: "Révisions", icon: "bolt" },
    { key: "oral_exercise", label: "Exercices oraux", icon: "comment-dots" },
    { key: "external_resource", label: "Ressources", icon: "link" },
]

export const ACTIVITY_PERIOD_FILTERS: ActivitySearchFilter<UIActivityPeriod>[] = [
    { key: "7d", label: "7 derniers jours", icon: "calendar-alt" },
    { key: "30d", label: "30 derniers jours", icon: "calendar-alt" },
    { key: "3m", label: "3 derniers mois", icon: "calendar-alt" },
]

export const EMPTY_ACTIVITY_SEARCH: UIActivitySearch = { query: "", type: null, subjectId: null, period: null }

type ActivitySearchChipField = "type" | "subjectId" | "period"

/** Toucher une puce : la poser, ou la retirer si c'était déjà elle. Une puce par groupe. */
export function toggleActivitySearch<F extends ActivitySearchChipField>(
    search: UIActivitySearch,
    field: F,
    key: NonNullable<UIActivitySearch[F]>
): UIActivitySearch {
    return { ...search, [field]: String(search[field]) === String(key) ? null : key }
}

export function isActivitySearchActive(search: UIActivitySearch): boolean {
    return search.query.trim() !== "" || search.type != null || search.subjectId != null || search.period != null
}

/**
 * Les paramètres de `GET activities/finished` pour une recherche : seuls les filtres posés
 * partent. Le serveur cherche dans toutes les activités faites, pas dans la page chargée.
 */
export type FinishedActivitiesParams = {
    title?: string
    type?: UIActivityType
    school_subject_id?: UISubject["id"]
    period?: UIActivityPeriod
}

export function toFinishedActivitiesParams(search: UIActivitySearch): FinishedActivitiesParams {
    const params: FinishedActivitiesParams = {}
    const title = search.query.trim()
    if (title !== "") params.title = title
    if (search.type != null) params.type = search.type
    if (search.subjectId != null) params.school_subject_id = search.subjectId
    if (search.period != null) params.period = search.period
    return params
}

/**
 * Les matières que la barre propose : celles dont le serveur dit qu'elles ont des activités faites
 * (`meta.school_subject_ids`), par ordre alphabétique.
 */
export function pickSearchSubjects<S extends { id?: string | number; name?: string }>(
    ids: unknown,
    subjects: S[]
): S[] {
    if (!Array.isArray(ids)) return []
    const wanted = new Set(ids.map(String))
    return subjects
        .filter((subject) => wanted.has(String(subject.id)))
        .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", "fr"))
}
